import {
    afterNextRender,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    Injector,
    OnDestroy,
    viewChild,
    viewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    isInsideSyntheticMasterNavEscapeDispatch,
    isSyntheticMasterNavEscapeEvent,
} from 'app/shared/directives/master-form-key-nav.logic';
import { WorkspaceTab, WorkspaceTabsService } from './workspace-tabs.service';
import { WorkspaceTabRouteGroup, workspaceTabsGroupedByTabKey } from './workspace-tab-module.util';
import { WorkspaceTabModuleGroupComponent } from './workspace-tab-module-group.component';

/**
 * Top strip of open workspace tabs.
 * One tab in a display group → flat chip; 2+ → one chip with a dropdown.
 */
@Component({
    selector: 'app-workspace-tabs-bar',
    templateUrl: './workspace-tabs-bar.component.html',
    styleUrl: './workspace-tabs-bar.component.scss',
    imports: [MatButtonModule, MatIconModule, MatTooltipModule, WorkspaceTabModuleGroupComponent],
})
export class WorkspaceTabsBarComponent implements OnDestroy {
    readonly workspaceTabs = inject(WorkspaceTabsService);
    private readonly _injector = inject(Injector);
    private readonly _tabsScroll = viewChild<ElementRef<HTMLElement>>('tabsScroll');
    private readonly _dropdowns = viewChildren(WorkspaceTabModuleGroupComponent);
    private readonly _onKeydown = (event: KeyboardEvent): void => this.onWindowKey(event, 'down');
    private readonly _onKeyup = (event: KeyboardEvent): void => this.onWindowKey(event, 'up');

    constructor() {
        // Window capture runs before list pages, which also listen on window.
        window.addEventListener('keydown', this._onKeydown, true);
        window.addEventListener('keyup', this._onKeyup, true);
        let prevTabCount = 0;
        effect(() => {
            const tabs = this.workspaceTabs.tabs();
            const n = tabs.length;
            if (n > prevTabCount) {
                afterNextRender(
                    () => {
                        const el = this._tabsScroll()?.nativeElement;
                        if (el) {
                            el.scrollLeft = 0;
                        }
                    },
                    { injector: this._injector },
                );
            }
            prevTabCount = n;
        });
    }

    readonly tabGroups = computed(() => workspaceTabsGroupedByTabKey(this.workspaceTabs.tabs()));

    ngOnDestroy(): void {
        window.removeEventListener('keydown', this._onKeydown, true);
        window.removeEventListener('keyup', this._onKeyup, true);
    }

    trackByTabGroupKey(_: number, group: WorkspaceTabRouteGroup): string {
        return group.tabKey;
    }

    /**
     * Alt+arrows belong to the tab strip on every page, including lists.
     * Stop them here so a list pager and the browser Back shortcut never see them.
     */
    private onWindowKey(event: KeyboardEvent, phase: 'down' | 'up'): void {
        if (!this.isAltArrow(event)) {
            if (phase === 'down') {
                this.onDropdownConfirmKey(event);
            }
            return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        if (phase === 'up') {
            return;
        }
        this.moveTabsByAltArrow(event.key);
    }

    private isAltArrow(event: KeyboardEvent): boolean {
        if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
            return false;
        }
        return (
            event.key === 'ArrowLeft' ||
            event.key === 'ArrowRight' ||
            event.key === 'ArrowUp' ||
            event.key === 'ArrowDown'
        );
    }

    private moveTabsByAltArrow(key: string): void {
        const groups = this.tabGroups();
        if (groups.length === 0) {
            return;
        }
        const activeId = this.workspaceTabs.activeTabId();
        let index = groups.findIndex((group) => group.tabs.some((tab) => tab.id === activeId));
        if (index < 0) {
            index = 0;
        }
        if (key === 'ArrowRight' || key === 'ArrowLeft') {
            const next = index + (key === 'ArrowRight' ? 1 : -1);
            const group = groups[next];
            const tab = group?.tabs[0];
            if (!group || !tab) {
                return;
            }
            this.closeOpenDropdowns();
            // The switch closes form dropdowns on a later tick (including a fake Escape).
            // Open this list after that pass so it stays up for the 6 second idle close.
            void this.workspaceTabs.selectTab(tab).then(() => {
                setTimeout(() => {
                    if (this.workspaceTabs.activeTabId() !== tab.id) {
                        return;
                    }
                    this.dropdownFor(group)?.openAt(0);
                }, 0);
            });
            return;
        }
        const current = groups[index];
        const dropdown = current ? this.dropdownFor(current) : undefined;
        if (!dropdown) {
            return;
        }
        if (key === 'ArrowDown') {
            dropdown.moveHighlight(1);
            return;
        }
        if (dropdown.isOpen()) {
            dropdown.moveHighlight(-1);
        }
    }

    /**
     * While a chip's list is open, Up and Down move its sub-tabs.
     * Enter selects the marked row only after those arrows, or when the key is on the chip.
     * Escape closes the list. A page such as Sales List keeps Enter for its own row.
     */
    private onDropdownConfirmKey(event: KeyboardEvent): void {
        // Tab switches dispatch Escape to close form dropdowns. That is not the user closing this list.
        if (
            isSyntheticMasterNavEscapeEvent(event) ||
            isInsideSyntheticMasterNavEscapeDispatch()
        ) {
            return;
        }
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }
        const open = this._dropdowns().find((dropdown) => dropdown.isOpen());
        if (!open) {
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            event.stopImmediatePropagation();
            open.moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopImmediatePropagation();
            open.closeMenu();
            return;
        }
        if (event.key === 'Enter' && open.ownsEnter(event)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            open.selectHighlighted();
        }
    }

    private dropdownFor(group: WorkspaceTabRouteGroup): WorkspaceTabModuleGroupComponent | undefined {
        return this._dropdowns().find((dropdown) => dropdown.group().tabKey === group.tabKey);
    }

    private closeOpenDropdowns(): void {
        for (const dropdown of this._dropdowns()) {
            if (dropdown.isOpen()) {
                dropdown.closeMenu();
            }
        }
    }

    select(tab: WorkspaceTab): void {
        this.workspaceTabs.selectTab(tab);
    }

    close(tab: WorkspaceTab, event: Event): void {
        void this.workspaceTabs.closeTab(tab, event);
    }
}
